import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { listDocuments, analyzeRisk, askAboutDocument, summarizeDocument, exportAnalysis, getDocument, getLegalEntities, updateLegalEntities, lookupCitation, translateDocument } from '../services/api';
import toast from 'react-hot-toast';

const RISK_BG: Record<string, string> = {
  low: 'bg-green-50 border-green-300 text-green-800',
  medium: 'bg-amber-50 border-amber-300 text-amber-800',
  high: 'bg-orange-50 border-orange-300 text-orange-800',
  critical: 'bg-red-50 border-red-300 text-red-800',
};

const CLAUSE_COLOR: Record<string, string> = {
  low: 'bg-green-100 text-green-700',
  medium: 'bg-amber-100 text-amber-700',
  high: 'bg-red-100 text-red-700',
};

const HEATMAP_COLOR: Record<string, string> = {
  low: 'bg-green-500',
  medium: 'bg-amber-500',
  high: 'bg-red-500',
};

export default function ContractReviewPage() {
  const { currentUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [docs, setDocs] = useState<any[]>([]);
  const [selectedDocId, setSelectedDocId] = useState<string>(searchParams.get('doc') || '');
  const [analysis, setAnalysis] = useState<any>(null);
  const [summary, setSummary] = useState<string>('');
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loadingAsk, setLoadingAsk] = useState(false);
  const [entities, setEntities] = useState<any>(null);
  const [loadingEntities, setLoadingEntities] = useState(false);
  const [translation, setTranslation] = useState<string>('');
  const [translateTo, setTranslateTo] = useState<'en' | 'zh-Hans' | 'zh-Hant'>('en');
  const [loadingTranslate, setLoadingTranslate] = useState(false);
  const [jurisdiction, setJurisdiction] = useState('HK');
  const [showDocText, setShowDocText] = useState(false);
  const [docText, setDocText] = useState('');
  const [loadingText, setLoadingText] = useState(false);

  const loadDocs = useCallback(async () => {
    if (!currentUser) return;
    try {
      const token = await currentUser.getIdToken();
      const res = await listDocuments(token);
      const docList = res.documents || [];
      setDocs(docList);

      // Auto-select the most recent doc if nothing is selected yet, so a
      // user returning to this page sees their cached analysis without
      // needing to re-pick from the dropdown. docList comes back ordered
      // DESCENDING by upload time (see backend list_documents).
      let activeId = selectedDocId;
      if (!activeId && docList.length > 0) {
        activeId = docList[0].doc_id;
        setSelectedDocId(activeId);
      }
      if (activeId) {
        const doc = docList.find((d: any) => d.doc_id === activeId);
        if (doc?.analysis) setAnalysis(doc.analysis);
        if (doc?.summary) setSummary(doc.summary);
        if (doc?.entities) setEntities(doc.entities);
        if (doc?.translations?.[translateTo]) setTranslation(doc.translations[translateTo]);
      }
    } catch { /* ok */ }
  }, [currentUser, selectedDocId, translateTo]);

  useEffect(() => { loadDocs(); }, [loadDocs]);

  const handleAnalyze = async () => {
    if (!selectedDocId || !currentUser) return;
    setLoadingAnalysis(true); setAnalysis(null);
    try {
      const token = await currentUser.getIdToken();
      const res = await analyzeRisk(token, selectedDocId, jurisdiction);
      setAnalysis(res.analysis);
      toast.success('Analysis complete!');
    } catch (e: any) { toast.error(e.message || 'Analysis failed'); }
    finally { setLoadingAnalysis(false); }
  };

  const handleExtractEntities = async () => {
    if (!selectedDocId || !currentUser) return;
    setLoadingEntities(true);
    try {
      const token = await currentUser.getIdToken();
      const res = await getLegalEntities(token, selectedDocId);
      setEntities(res.entities);
    } catch (e: any) { toast.error(e.message || 'Failed'); }
    finally { setLoadingEntities(false); }
  };

  // Optimistically update local state, then persist. Single source of truth
  // for both add and delete — `next` is the new full entities dict.
  const persistEntities = async (next: any) => {
    if (!selectedDocId || !currentUser) return;
    setEntities(next);
    try {
      const token = await currentUser.getIdToken();
      await updateLegalEntities(token, selectedDocId, next);
    } catch (e: any) {
      toast.error(e.message || 'Save failed');
    }
  };

  const handleAddEntity = (category: string, value: string) => {
    const v = value.trim();
    if (!v || !entities) return;
    const list = entities[category] || [];
    if (list.includes(v)) return;
    persistEntities({ ...entities, [category]: [...list, v] });
  };

  const handleDeleteEntity = (category: string, value: string) => {
    if (!entities) return;
    const list = (entities[category] || []).filter((x: string) => x !== value);
    persistEntities({ ...entities, [category]: list });
  };

  const findBacklinks = async (category: string, value: string) => {
    if (!currentUser) return { docs: [] };
    const token = await currentUser.getIdToken();
    return lookupCitation(token, category, value);
  };

  const handleTranslate = async () => {
    if (!selectedDocId || !currentUser) return;
    setLoadingTranslate(true); setTranslation('');
    try {
      const token = await currentUser.getIdToken();
      const res = await translateDocument(token, selectedDocId, translateTo);
      setTranslation(res.translation);
    } catch (e: any) { toast.error(e.message || 'Translation failed'); }
    finally { setLoadingTranslate(false); }
  };

  const handleSummarize = async () => {
    if (!selectedDocId || !currentUser) return;
    setLoadingSummary(true); setSummary('');
    try {
      const token = await currentUser.getIdToken();
      const res = await summarizeDocument(token, selectedDocId);
      setSummary(res.summary);
    } catch (e: any) { toast.error(e.message || 'Failed'); }
    finally { setLoadingSummary(false); }
  };

  const handleAsk = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!question.trim() || !currentUser) return;
    setLoadingAsk(true); setAnswer('');
    try {
      const token = await currentUser.getIdToken();
      const res = await askAboutDocument(token, question, selectedDocId || undefined);
      setAnswer(res.answer);
    } catch (e: any) { toast.error(e.message || 'Failed'); }
    finally { setLoadingAsk(false); }
  };

  const handleExport = async () => {
    if (!selectedDocId || !currentUser) return;
    try {
      const token = await currentUser.getIdToken();
      const res = await exportAnalysis(token, selectedDocId);
      const blob = new Blob([JSON.stringify(res.report, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `lexquant-report-${selectedDocId.slice(0, 8)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Report exported!');
    } catch (e: any) { toast.error(e.message || 'Export failed'); }
  };

  const handleViewText = async () => {
    if (!selectedDocId || !currentUser) return;
    if (showDocText) { setShowDocText(false); return; }
    setLoadingText(true);
    try {
      const token = await currentUser.getIdToken();
      const res = await getDocument(token, selectedDocId, true);
      setDocText(res.document?.extracted_text || 'No text available');
      setShowDocText(true);
    } catch (e: any) { toast.error(e.message || 'Failed to load text'); }
    finally { setLoadingText(false); }
  };

  const selectedDoc = docs.find((d) => d.doc_id === selectedDocId);

  // Compute clause risk distribution for heatmap
  const clauseCounts = analysis?.key_clauses?.reduce((acc: any, c: any) => {
    acc[c.risk] = (acc[c.risk] || 0) + 1;
    return acc;
  }, {} as Record<string, number>) || {};
  const totalClauses = Object.values(clauseCounts).reduce((a: number, b: any) => a + b, 0) as number;

  return (
    <div className="max-w-5xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Contract Review</h1>
        <p className="text-gray-500 text-sm mt-1">AI risk analysis, plain-English summary, and Q&A on your documents.</p>
      </div>

      {/* Document selector + controls */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-48">
          <label className="block text-xs font-medium text-gray-600 mb-1">Select Document</label>
          <select value={selectedDocId} onChange={(e) => { setSelectedDocId(e.target.value); setAnalysis(null); setSummary(''); setAnswer(''); setShowDocText(false); setEntities(null); setTranslation(''); }}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="">— Select a document —</option>
            {docs.map((d) => <option key={d.doc_id} value={d.doc_id}>{d.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Jurisdiction</label>
          <select value={jurisdiction} onChange={(e) => setJurisdiction(e.target.value)}
            className="px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
            {['HK', 'UK', 'US', 'SG', 'AU', 'CN'].map((j) => <option key={j} value={j}>{j}</option>)}
          </select>
        </div>
        <button onClick={handleAnalyze} disabled={!selectedDocId || loadingAnalysis}
          className="px-5 py-2.5 bg-blue-700 text-white rounded-lg text-sm font-semibold hover:bg-blue-600 disabled:opacity-50 transition-colors">
          {loadingAnalysis ? 'Analysing...' : '⚖ Risk Analysis'}
        </button>
        <button onClick={handleSummarize} disabled={!selectedDocId || loadingSummary}
          className="px-5 py-2.5 bg-slate-700 text-white rounded-lg text-sm font-semibold hover:bg-slate-600 disabled:opacity-50 transition-colors">
          {loadingSummary ? 'Summarising...' : '📝 Summarise'}
        </button>
        <button onClick={handleExtractEntities} disabled={!selectedDocId || loadingEntities}
          className="px-5 py-2.5 border border-slate-300 text-slate-700 rounded-lg text-sm font-semibold hover:bg-slate-50 disabled:opacity-50 transition-colors">
          {loadingEntities ? 'Extracting...' : '⚓ Citations & Statutes'}
        </button>
        <div className="flex">
          <select value={translateTo} onChange={(e) => setTranslateTo(e.target.value as any)}
            className="px-2 py-2.5 border border-r-0 border-gray-300 rounded-l-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="en">→ English</option>
            <option value="zh-Hant">→ 繁體</option>
            <option value="zh-Hans">→ 简体</option>
          </select>
          <button onClick={handleTranslate} disabled={!selectedDocId || loadingTranslate}
            className="px-4 py-2.5 border border-gray-300 text-gray-700 rounded-r-lg text-sm font-semibold hover:bg-gray-50 disabled:opacity-50 transition-colors">
            {loadingTranslate ? 'Translating...' : '🌐 Translate'}
          </button>
        </div>
        <button onClick={handleViewText} disabled={!selectedDocId || loadingText}
          className="px-4 py-2.5 border border-gray-300 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50 transition-colors">
          {loadingText ? '...' : showDocText ? 'Hide Text' : '📄 View Text'}
        </button>
        {analysis && (
          <button onClick={handleExport}
            className="px-4 py-2.5 border border-gray-300 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors">
            📥 Export
          </button>
        )}
      </div>

      {/* Document text panel */}
      {showDocText && (
        <div className="bg-white rounded-2xl border border-gray-200 p-5">
          <h2 className="font-semibold text-gray-800 mb-3">📄 Document Text</h2>
          <div className="max-h-96 overflow-y-auto bg-gray-50 rounded-xl p-4 text-xs text-gray-700 leading-relaxed whitespace-pre-wrap font-mono">
            {docText}
          </div>
        </div>
      )}

      {/* Risk Analysis Results */}
      {analysis && (
        <div className="space-y-4">
          {/* Score banner */}
          <div className={`rounded-2xl border p-5 ${RISK_BG[analysis.risk_level] || 'bg-gray-50 border-gray-300 text-gray-800'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium opacity-70">Risk Score</p>
                <p className="text-5xl font-extrabold">{analysis.risk_score}<span className="text-2xl">/100</span></p>
                <p className="text-lg font-semibold capitalize mt-1">{analysis.risk_level} risk</p>
              </div>
              <div className="text-right max-w-xs">
                <p className="text-sm leading-relaxed opacity-80">{analysis.summary}</p>
              </div>
            </div>
          </div>

          {/* Risk Heatmap Bar */}
          {totalClauses > 0 && (
            <div className="bg-white rounded-2xl border border-gray-200 p-5">
              <h2 className="font-semibold text-gray-800 mb-3">Clause Risk Distribution</h2>
              <div className="flex rounded-full overflow-hidden h-4">
                {(['low', 'medium', 'high'] as const).map((level) => {
                  const count = clauseCounts[level] || 0;
                  if (!count) return null;
                  const pct = (count / totalClauses) * 100;
                  return (
                    <div key={level} className={`${HEATMAP_COLOR[level]} relative group`}
                      style={{ width: `${pct}%` }}>
                      <span className="absolute inset-0 flex items-center justify-center text-xs text-white font-bold opacity-0 group-hover:opacity-100 transition-opacity">
                        {count}
                      </span>
                    </div>
                  );
                })}
              </div>
              <div className="flex gap-4 mt-2 text-xs text-gray-500">
                {(['low', 'medium', 'high'] as const).map((level) => (
                  clauseCounts[level] ? (
                    <span key={level} className="flex items-center gap-1">
                      <span className={`w-2.5 h-2.5 rounded-full ${HEATMAP_COLOR[level]}`} />
                      {level}: {clauseCounts[level]}
                    </span>
                  ) : null
                ))}
              </div>
            </div>
          )}

          {/* Key clauses */}
          {analysis.key_clauses?.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-200 p-5">
              <h2 className="font-semibold text-gray-800 mb-3">Key Clauses</h2>
              <div className="space-y-3">
                {analysis.key_clauses.map((c: any, i: number) => (
                  <div key={i} className="border border-gray-100 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-sm text-gray-800">{c.clause_name}</span>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${CLAUSE_COLOR[c.risk] || 'bg-gray-100 text-gray-600'}`}>
                        {c.risk}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">{c.explanation}</p>
                    {c.quote && (
                      <blockquote className="mt-2 pl-3 border-l-2 border-gray-300 text-xs text-gray-400 italic line-clamp-2">
                        "{c.quote}"
                      </blockquote>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Red flags & recommendations */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {analysis.red_flags?.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-2xl p-5">
                <h2 className="font-semibold text-red-800 mb-2">🚩 Red Flags</h2>
                <ul className="space-y-1">
                  {analysis.red_flags.map((f: string, i: number) => (
                    <li key={i} className="text-sm text-red-700 flex gap-2"><span>•</span>{f}</li>
                  ))}
                </ul>
              </div>
            )}
            {analysis.recommendations?.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5">
                <h2 className="font-semibold text-blue-800 mb-2">💡 Recommendations</h2>
                <ul className="space-y-1">
                  {analysis.recommendations.map((r: string, i: number) => (
                    <li key={i} className="text-sm text-blue-700 flex gap-2"><span>•</span>{r}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {analysis.jurisdiction_notes && (
            <div className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-700">
              <span className="font-medium">Jurisdiction notes: </span>{analysis.jurisdiction_notes}
            </div>
          )}
        </div>
      )}

      {/* Summary */}
      {summary && (
        <div className="bg-white rounded-2xl border border-gray-200 p-5">
          <h2 className="font-semibold text-gray-800 mb-3">📝 Plain-English Summary</h2>
          <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{summary}</p>
        </div>
      )}

      {/* Translation */}
      {translation && (
        <div className="bg-white rounded-2xl border border-gray-200 p-5">
          <h2 className="font-semibold text-gray-800 mb-3">
            🌐 Translation
            <span className="ml-2 text-xs font-normal text-gray-400">
              → {translateTo === 'en' ? 'English' : translateTo === 'zh-Hant' ? '繁體中文' : '简体中文'}
            </span>
          </h2>
          <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{translation}</p>
        </div>
      )}

      {/* Citations, statutes, cases — HK / UK common-law entity extraction */}
      {entities && (
        <div className="bg-white rounded-2xl border border-gray-200 p-5">
          <h2 className="font-semibold text-gray-800 mb-3">⚓ Citations & Statutes</h2>
          <p className="text-xs text-gray-400 mb-3">Click a citation or case to see other docs in your vault that cite it. × removes; the input below each list adds new entries.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <EntityList category="hk_citations" title="HK Citations" items={entities.hk_citations} color="text-blue-700 bg-blue-50 border-blue-200"
              onAdd={(v) => handleAddEntity('hk_citations', v)} onDelete={(v) => handleDeleteEntity('hk_citations', v)} onLookup={findBacklinks} />
            <EntityList category="uk_citations" title="UK Citations" items={entities.uk_citations} color="text-purple-700 bg-purple-50 border-purple-200"
              onAdd={(v) => handleAddEntity('uk_citations', v)} onDelete={(v) => handleDeleteEntity('uk_citations', v)} onLookup={findBacklinks} />
            <EntityList category="statutes" title="Statutes (Cap.)" items={entities.statutes} color="text-emerald-700 bg-emerald-50 border-emerald-200"
              onAdd={(v) => handleAddEntity('statutes', v)} onDelete={(v) => handleDeleteEntity('statutes', v)} />
            <EntityList category="provisions" title="Sections / Provisions" items={entities.provisions} color="text-amber-700 bg-amber-50 border-amber-200"
              onAdd={(v) => handleAddEntity('provisions', v)} onDelete={(v) => handleDeleteEntity('provisions', v)} />
            <div className="md:col-span-2">
              <EntityList category="cases" title="Case names" items={entities.cases} color="text-slate-700 bg-slate-50 border-slate-200"
                onAdd={(v) => handleAddEntity('cases', v)} onDelete={(v) => handleDeleteEntity('cases', v)} onLookup={findBacklinks} />
            </div>
          </div>
        </div>
      )}

      {/* Q&A */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5">
        <h2 className="font-semibold text-gray-800 mb-3">Ask a Question</h2>
        <form onSubmit={handleAsk} className="flex gap-2 mb-4">
          <input type="text" value={question} onChange={(e) => setQuestion(e.target.value)}
            placeholder={selectedDoc ? `Ask about "${selectedDoc.label}"...` : 'Ask a general legal question...'}
            className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={loadingAsk} />
          <button type="submit" disabled={!question.trim() || loadingAsk}
            className="px-5 py-2.5 bg-blue-700 text-white rounded-lg text-sm font-medium hover:bg-blue-600 disabled:opacity-50 transition-colors">
            {loadingAsk ? '...' : 'Ask'}
          </button>
        </form>
        {answer && (
          <div className="bg-slate-50 rounded-xl p-4 text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
            {answer}
          </div>
        )}
      </div>
    </div>
  );
}

interface EntityListProps {
  category: string;
  title: string;
  items?: string[];
  color: string;
  onAdd: (value: string) => void;
  onDelete: (value: string) => void;
  onLookup?: (category: string, value: string) => Promise<{ docs: { doc_id: string; label: string }[] }>;
}

function EntityList({ category, title, items, color, onAdd, onDelete, onLookup }: EntityListProps) {
  const [adding, setAdding] = useState('');
  const [openLookup, setOpenLookup] = useState<string | null>(null);
  const [lookupDocs, setLookupDocs] = useState<{ doc_id: string; label: string }[]>([]);
  const [loadingLookup, setLoadingLookup] = useState(false);
  const list = items || [];

  const handlePillClick = async (item: string) => {
    if (!onLookup) return;
    if (openLookup === item) { setOpenLookup(null); return; }
    setOpenLookup(item);
    setLoadingLookup(true);
    try {
      const res = await onLookup(category, item);
      setLookupDocs(res.docs || []);
    } catch {
      setLookupDocs([]);
    } finally {
      setLoadingLookup(false);
    }
  };

  const submitAdd = (e: React.FormEvent) => {
    e.preventDefault();
    if (!adding.trim()) return;
    onAdd(adding);
    setAdding('');
  };

  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
        {title} <span className="text-gray-400 normal-case">· {list.length}</span>
      </p>
      <div className="flex flex-wrap gap-1.5">
        {list.map((item, i) => (
          <span key={`${item}-${i}`}
            className={`group inline-flex items-center text-xs rounded border ${color} ${onLookup ? 'cursor-pointer' : ''}`}>
            <span onClick={() => handlePillClick(item)} className="px-2 py-0.5">
              {item}
            </span>
            <button onClick={() => onDelete(item)}
              className="px-1.5 py-0.5 text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
              aria-label="Remove">×</button>
          </span>
        ))}
      </div>
      {/* Backlink results — inline expand */}
      {openLookup && (
        <div className="mt-2 ml-2 border-l-2 border-gray-200 pl-3 text-xs">
          {loadingLookup ? (
            <span className="text-gray-400">Searching…</span>
          ) : lookupDocs.length === 0 ? (
            <span className="text-gray-400">No other docs cite "{openLookup}".</span>
          ) : (
            <div>
              <p className="text-gray-500 mb-1">{lookupDocs.length} other doc{lookupDocs.length > 1 ? 's' : ''} cite "{openLookup}":</p>
              <ul className="space-y-0.5">
                {lookupDocs.map((d) => (
                  <li key={d.doc_id} className="text-gray-700 truncate">• {d.label}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      <form onSubmit={submitAdd} className="mt-2">
        <input type="text" value={adding} onChange={(e) => setAdding(e.target.value)}
          placeholder={`+ Add ${title.toLowerCase()}`}
          className="w-full px-2 py-1 border border-gray-200 rounded text-xs placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-400" />
      </form>
    </div>
  );
}
